import type { NavigationItem, ZudokuPlugin } from "zudoku";
import { isUnder, type Reference } from "./define";

/*
  Sidebar entries for the JS references.

  The OpenAPI plugin appends one category per tag while you are on an API
  page: the category links to the tag page, and the operations of the tag are
  anchor links inside it. This plugin does the same for a reference, one
  category per page: "Information" links to the overview page and holds one
  anchor link per topic (Errors, Expand parameters, ...), then one category per
  group holds one anchor link per method. Every category starts open, as the
  API categories do. See createNavigationCategory.tsx in the OpenAPI plugin.

  Zudoku opens the category whose `link.path` is the current path, and marks an
  anchor link active while its heading is the one its own observer reports;
  the markdown `##` headings and the <Method> heading register themselves, so
  the highlight follows the reader.

  Every page is a category on purpose. A plain link to a page counts as active
  whenever the page is open, so a page that also has anchor links would show
  two active entries: the page link and the section in view. A category link
  is styled as a title instead, and only the section in view is highlighted.
*/

/** The sidebar of one reference: a category per page, anchor links inside. */
export const sidebarOf = (reference: Reference): NavigationItem[] => {
  const page = (
    label: string,
    path: string,
    sections: readonly { slug: string; label: string }[],
  ): NavigationItem => ({
    type: "category",
    label,
    link: { type: "doc", path, file: path.replace(/^\//, ""), label },
    collapsible: true,
    collapsed: false,
    items: sections.map(({ slug, label }) => ({
      type: "link",
      label,
      to: `${path}#${slug}`,
    })),
  });

  return [
    page("Information", reference.path, reference.topics),
    ...reference.groups.map((group) =>
      page(group.label, reference.pathOf(group), group.items),
    ),
  ];
};

export const referenceNavigation = (
  references: readonly Reference[],
): ZudokuPlugin => ({
  // The group pages are `doc` entries in the config, so they are routed there.
  getRoutes: () => [],
  getNavigation: async (path): Promise<NavigationItem[]> => {
    const reference = references.find((candidate) =>
      isUnder(path, candidate.path),
    );
    return reference ? sidebarOf(reference) : [];
  },
});
